import { parseGraphParams, type ParsedPlot } from "./graphParams";
import { parseMultiTimeMode } from "./buildMultiTimeGraphParams";
import { PLOT_COLOURS } from "./plotColours";
import { formatTigerDateTime } from "./tigerTime";
import { getEventModelTimes } from "../api/getEventModelTimes";
import { getChannelInfo, type ChannelLookup } from "../api/getChannelInfo";
import { getViewRunState } from "./viewMode";
import { getTigerResource, tigerFormat } from "./tigerResource";

/**
 * One plot's row in an embedded graph's hover tooltip. Mirrors the C++
 * `tiger_graph::get_tooltip_str`, which aggregates one
 * `tiger_plot::get_plot_tooltip_str` line per plot (channel, description, state,
 * colour). Multi-time plots additionally carry a start→end window
 * (`tiger_multitime_plot::get_plot_tooltip_str`); other graph types leave
 * `timeLabel` empty.
 */
export interface GraphPlotTip {
  /** Channel name (config stripped), or "Y vs X" for XY plots. */
  label: string;
  /** Channel description; "" when unknown or identical to the channel name. */
  description: string;
  /** Plot stroke colour (hex or CSS name), used for the swatch. */
  colour: string;
  /** Per-plot turbine state (e.g. "BASE_LOAD"); "" / "DEFAULT" means none shown. */
  state: string;
  /**
   * Right-aligned time text: "" (no window — regular/trend/XY graphs),
   * "Current" (live multi-time plot), "…" (resolving), or "start → end".
   */
  timeLabel: string;
}

function stripConfig(tag: string): string {
  const i = tag.indexOf(":");
  return i < 0 ? tag : tag.slice(i + 1);
}

function configOf(tag: string): string {
  const i = tag.indexOf(":");
  return i < 0 ? "" : tag.slice(0, i);
}

/** Channel name without config prefix or a trailing "(AVG)"-style trend suffix. */
function baseChannel(name: string): string {
  return stripConfig(name).replace(/\s*\([^)]*\)\s*$/, "");
}

/** The channel a row describes — the y-channel for an XY pair, else the tag. */
function primaryTag(p: ParsedPlot): string {
  return p.xTagName && p.yTagName ? p.yTagName : (p.tagName ?? "");
}

function plotLabel(p: ParsedPlot): string {
  if (p.xTagName && p.yTagName) {
    return tigerFormat(
      getTigerResource("TIGER_GRAPH_X_VS_Y"),
      stripConfig(p.yTagName),
      stripConfig(p.xTagName),
    );
  }
  return stripConfig(p.tagName ?? "");
}

/** Seed time text from params alone: live plots show "Current", historical
 *  multi-time plots show a "…" placeholder until {@link resolveGraphTooltip}
 *  fills the real window, and everything else shows nothing. */
function seedTimeLabel(p: ParsedPlot): string {
  if (p.multitimeMode == null) return "";
  return parseMultiTimeMode(p.multitimeMode) === "CURRENT"
    ? getTigerResource("TIGER_GRAPH_MULTI_CUR")
    : "…";
}

/**
 * Synchronous tooltip rows from the params string alone: colour, label, state
 * and the live/historical time placeholder. Lets the tooltip appear instantly
 * on hover while {@link resolveGraphTooltip} fills descriptions and the real
 * multi-time windows.
 */
export function buildGraphTooltipSeed(params: string): GraphPlotTip[] {
  const parsed = parseGraphParams(params);
  return parsed.plots.map((p, i) => ({
    label: plotLabel(p),
    description: "",
    colour: p.colour || PLOT_COLOURS[i % PLOT_COLOURS.length],
    state: p.turbineState ?? "",
    timeLabel: seedTimeLabel(p),
  }));
}

/**
 * Full tooltip rows: seeds enriched with channel descriptions and, for
 * historical multi-time plots, the resolved wall-clock window. The window comes
 * from `getEventModelTimes(config, sessionType, model)[relIndex]` — the same
 * path `GraphWindow`'s legend uses — because the embedded graph's series data is
 * pre-rebased to event-relative seconds by the backend, so the real window has
 * to be recovered from the event model rather than the plotted data. Non
 * multi-time graphs keep an empty `timeLabel`. Description lookup is best-effort.
 */
export async function resolveGraphTooltip(params: string): Promise<GraphPlotTip[]> {
  const parsed = parseGraphParams(params);
  if (parsed.plots.length === 0) return [];
  const seed = buildGraphTooltipSeed(params);

  // Descriptions: one batched round-trip, best-effort. Unknown channels resolve
  // to their own name, which we then drop (leaving just the label).
  const lookups: ChannelLookup[] = parsed.plots.map((p) => {
    const src = primaryTag(p);
    return { config_name: configOf(src), channel_name: baseChannel(src) };
  });
  let infos: Awaited<ReturnType<typeof getChannelInfo>>;
  try {
    infos = await getChannelInfo(lookups);
  } catch {
    infos = [];
  }

  // Multi-time windows resolve against the run-state source — OFFLINE in replay,
  // ONLINE in live — mirroring the backend's resolve_plot_time_range and C++
  // tiger_graph__get_source_string(). The graph param's session_type is "HIST"
  // for every view multi-time widget; using it here showed the HIST archive's
  // latest events (e.g. 2024) even when a 2021 window was loaded in replay.
  const needsEventTimes = parsed.plots.some((p) => (p.multitimeModelEvent ?? "") !== "");
  const eventSource = needsEventTimes
    ? ((await getViewRunState()) === "replay" ? "OFFLINE" : "ONLINE")
    : "";

  // Multi-time historical windows: resolve event occurrences once per (config, model).
  const occCache = new Map<string, { start: number; end: number }[]>();
  const occKey = (cfg: string, model: string) => `${cfg}|${model}`;
  await Promise.all(
    parsed.plots.map(async (p) => {
      const model = p.multitimeModelEvent ?? "";
      if (!model) return;
      const cfg = configOf(p.tagName ?? "");
      const key = occKey(cfg, model);
      if (occCache.has(key)) return;
      try {
        occCache.set(key, await getEventModelTimes(cfg, eventSource, model));
      } catch {
        occCache.set(key, []);
      }
    }),
  );

  return parsed.plots.map((p, i) => {
    const base = baseChannel(primaryTag(p));
    const infoDesc = infos[i]?.description ?? "";
    const description = infoDesc && infoDesc !== base ? infoDesc : "";

    let timeLabel = seed[i].timeLabel;
    if (p.multitimeMode != null && parseMultiTimeMode(p.multitimeMode) !== "CURRENT") {
      const relIdx = p.multitimeRelativeIndex ? parseInt(p.multitimeRelativeIndex, 10) : 0;
      const cfg = configOf(p.tagName ?? "");
      const model = p.multitimeModelEvent ?? "";
      const occ = (occCache.get(occKey(cfg, model)) ?? [])[Number.isFinite(relIdx) ? relIdx : 0];
      const start = occ?.start ?? 0;
      const end = occ?.end ?? 0;
      timeLabel =
        start > 0 || end > 0
          ? `${start > 0 ? formatTigerDateTime(start) : "?"} → ${end > 0 ? formatTigerDateTime(end) : "?"}`
          : "";
    }
    return { ...seed[i], description, timeLabel };
  });
}
